import {
  Divider,
  GKButton,
  Grid2,
  PlusIcon,
  PopConfirm,
  Typography,
} from "components";
import { useFetchQuery } from "hooks/useQueries";
import { useTransactionAction } from "hooks/useTransactionAction";
import {
  CompaniesListInterface,
  CompaniesResponseInterface,
} from "interfaces/PrivateRouteInterface";
import {
  CreateTransactionInterface,
  Transaction,
} from "interfaces/Transaction";
import DashboardLayout from "layouts/DashboardLayout";
import { useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useLocation, useParams } from "react-router-dom";
import {
  EQUITY_ETF_TRANSACTION_BY_ID,
  GET_ALL_OTHER_ASSET_BY_ASSET_TYPE_LIST_BY_PORTFOLIO_ID,
  GET_COMPANY_LIST,
} from "utils/apiRoutes";
import { QueryKeys } from "utils/queryKey";
import useDocumentTitle from "utils/useDocumentTitle";
import ClientModal from "./ClientModal";
import EquityEtfBody from "./EquityEtfBody";
import EquityFormHeader from "./EquityFormHeader";

const EquityEtfForm = () => {
  useDocumentTitle("Transaction");

  const { id } = useParams();
  const { state, search } = useLocation();

  const anchorTransaction = new URLSearchParams(search).get("anchor");

  const getTransactionType = state?.transactionType === "OTHER-ASSETS";
  const getAssetType = state?.assetType;

  const {
    handleCreate,
    createLoading,
    handleUpdateStock,
    mutationUpdateLoading,
    deleteLoading,
    handleDelete,
  } = useTransactionAction();

  const [isPortfolioCreateModalOpen, setIsPortfolioCreateModalOpen] =
    useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] =
    useState<HTMLElement | null>(null);
  const [companies, setCompanies] = useState([]);

  const { isFetching: isLoading } = useFetchQuery({
    key: ["COMPANY_LIST"],
    route: GET_COMPANY_LIST(getAssetType),
    success: (data: CompaniesResponseInterface) => {
      setCompanies(
        data?.data?.data?.map((item: CompaniesListInterface) => ({
          ...item,
          name: item.name,
          value: item.gridCode,
        })),
      );
    },
  });

  const { data, isFetching } = useFetchQuery({
    key: [QueryKeys.TRANSACTIONS_LIST, id],
    route: getTransactionType
      ? GET_ALL_OTHER_ASSET_BY_ASSET_TYPE_LIST_BY_PORTFOLIO_ID(
          id,
          getAssetType?.length > 1 ? getAssetType[0] : getAssetType,
        )
      : EQUITY_ETF_TRANSACTION_BY_ID(id),
    enabled: !!id,
  });

  // const form = useForm<CreateTransactionInterface>({
  //   defaultValues: {
  //     portfolio: (data?.data ? data.data.portfolio : +state?.id) || null,
  //     date: "",
  //     brokerName: "",
  //     contractNoteNum: "",
  //     anchor: false,
  //     transactions: [
  //       {
  //         brokerage: 0,
  //         gridCode: state?.gridCode || "",
  //         transactionType: "",
  //         amountWithBrokerage: 0,
  //         billAmount: 0,
  //         price: 0,
  //         quantity: 0,
  //         totalTransactionCharges: 0,
  //         cashEntry: false,
  //         totalCharges: {
  //           gst: 0,
  //           otherCharges: 0,
  //           stampCharges: 0,
  //           stt: 0,
  //           transactionCharges: 0,
  //           sebiTurnoverFees: 0,
  //         },
  //       },
  //     ],
  //   },
  //   values: {
  //     ...data?.data,
  //     portfolio: (data?.data ? data.data.portfolio : +state?.id) || null,
  //     anchor: anchorTransaction === "true",
  //     transactions: data?.data?.transactions?.map((dataItem: Transaction) => {
  //       const {
  //         totalCharges: {
  //           gst,
  //           otherCharges,
  //           sebiTurnoverFees,
  //           stampCharges,
  //           stt,
  //           transactionCharges,
  //         },
  //       } = dataItem;

  //       return {
  //         ...dataItem,
  //         totalTransactionCharges:
  //           +gst +
  //           +otherCharges +
  //           +sebiTurnoverFees +
  //           +stampCharges +
  //           +stt +
  //           +transactionCharges,
  //       };
  //     }),
  //   },
  // });

  const form = useForm<CreateTransactionInterface>({
    defaultValues: {
      portfolio: state?.id ? +state?.id : null,
      date: "",
      brokerName: "",
      contractNoteNum: "",
      anchor: anchorTransaction === "true",
      transactions: [
        {
          brokerage: 0,
          gridCode: state?.gridCode || "",
          transactionType: "",
          amountWithBrokerage: 0,
          billAmount: 0,
          price: 0,
          quantity: 0,
          totalTransactionCharges: 0,
          cashEntry: false,
          totalCharges: {
            gst: 0,
            otherCharges: 0,
            stampCharges: 0,
            stt: 0,
            transactionCharges: 0,
            sebiTurnoverFees: 0,
          },
        },
      ],
    },
  });

  useEffect(() => {
    if (data?.data) {
      form.reset({
        ...data.data,
        portfolio: data.data.portfolio || +state?.id || null,
        anchor: anchorTransaction === "true",
        transactions:
          data.data.transactions?.map((dataItem: Transaction) => {
            const {
              gst,
              otherCharges,
              sebiTurnoverFees,
              stampCharges,
              stt,
              transactionCharges,
            } = dataItem.totalCharges;

            return {
              ...dataItem,
              totalTransactionCharges:
                +gst +
                +otherCharges +
                +sebiTurnoverFees +
                +stampCharges +
                +stt +
                +transactionCharges,
            };
          }) ?? [], // fallback if no transactions
      });
    }
  }, [data, form.reset, anchorTransaction, state?.id]);

  const watchedTransactions = useWatch({
    control: form.control,
    name: "transactions",
  });

  const getTransactionValue = (index: number) => {
    const transaction = watchedTransactions[index];

    if (!transaction) return "0.00"; // Handle undefined transactions

    const {
      transactionType,
      quantity,
      price,
      totalTransactionCharges,
      brokerage,
    } = transaction;

    const transactionTotal = (Number(quantity) || 0) * (Number(price) || 0);
    const chargesTotal =
      (Number(totalTransactionCharges) || 0) + (Number(brokerage) || 0);

    const finalAmount = ["SELL", "BUY_BACK"].includes(transactionType)
      ? transactionTotal - chargesTotal
      : transactionTotal + chargesTotal;

    return finalAmount.toFixed(2);
  };

  return (
    <DashboardLayout
      title={id ? "Update Trade" : "Create Trade"}
      loading={isFetching || isLoading}
    >
      <Grid2
        container
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        gap={1}
      >
        <Typography fontWeight={600} fontSize={20}>
          {id ? "Update Trade" : "Add Trade"}
        </Typography>

        {!state?.id && (
          <GKButton
            variant="outlined"
            size="medium"
            color="dark"
            onClick={() => setIsPortfolioCreateModalOpen(true)}
            startIcon={<PlusIcon />}
          >
            Create New Portfolio
          </GKButton>
        )}
      </Grid2>

      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit((values) => {
            const updateData = {
              ...values,
              transactions: values?.transactions?.map((dataItem: any) => {
                return {
                  ...dataItem,
                  cashEntry: dataItem?.cashEntry || false,
                };
              }),
            };
            id ? handleUpdateStock(updateData) : handleCreate(updateData);
          })}
        >
          <Grid2 container spacing={2} mt={1}>
            <Grid2 size={12}>
              <EquityFormHeader />
            </Grid2>
            <Grid2 size={12}>
              <EquityEtfBody
                getTransactionValue={getTransactionValue}
                companies={companies}
                loading={isLoading}
              />
            </Grid2>
            <Grid2 size={12}>
              <Divider />
            </Grid2>
            <Grid2
              size={12}
              display={"flex"}
              justifyContent={"flex-end"}
              alignItems={"center"}
              gap={1}
            >
              {id && (
                <GKButton
                  variant="outlined"
                  size="medium"
                  color="error"
                  loading={deleteLoading}
                  onClick={(event) => setIsDeleteModalOpen(event.currentTarget)}
                >
                  Delete Trade
                </GKButton>
              )}
              <GKButton
                variant="contained"
                size="medium"
                type="submit"
                loading={createLoading || mutationUpdateLoading}
              >
                {id ? "Update" : "Save"}
              </GKButton>
            </Grid2>
          </Grid2>
        </form>
      </FormProvider>

      {isPortfolioCreateModalOpen && (
        <ClientModal
          isModalOpen={isPortfolioCreateModalOpen}
          setIsModalOpen={setIsPortfolioCreateModalOpen}
        />
      )}

      {isDeleteModalOpen && (
        <PopConfirm
          anchorEl={isDeleteModalOpen}
          open={Boolean(isDeleteModalOpen)}
          setOpen={setIsDeleteModalOpen}
          onSuccess={() => handleDelete(id)}
        />
      )}
    </DashboardLayout>
  );
};

export default EquityEtfForm;
